// eslint-disable-next-line max-classes-per-file
import { css, styled } from 'canoe';
import React from 'react';

import { Axes, Movement } from './types';

const StyledLine = styled.div<{ cursor: string; isMappedOverSource: boolean }>`
  ${({ theme, cursor, isMappedOverSource }) => css`
    position: absolute;
    z-index: 2;
    pointer-events: all;

    background-color: ${isMappedOverSource
      ? theme.colors.MangoOrangeD10
      : theme.colors.BrandPurple};

    &:hover {
      cursor: ${cursor}-resize;
    }
  `}
`;

const StyledDot = styled.div<{ cursor: string; position: string; isMappedOverSource: boolean }>`
  ${({ theme, cursor, position, isMappedOverSource }) => css`
    position: absolute;
    z-index: 10;
    pointer-events: all;

    ${
      cursor === 'ew'
        ? css`
            width: 6px;
            height: 16px;
          `
        : css`
            width: 16px;
            height: 6px;
          `
    }

    ${
      (cursor === 'nwse' || cursor === 'nesw') &&
      css`
        width: 19px;

        &:after {
          content: '';
          height: 19px;
          width: 6px;
          position: absolute;

          ${
            position === 'nw' &&
            css`
              top: 0;
              left: 0;
            `
          }
          ${
            position === 'ne' &&
            css`
              top: 0;
              right: 0;
            `
          }
          ${
            position === 'sw' &&
            css`
              bottom: 0;
              left: 0;
            `
          }
          ${
            position === 'se' &&
            css`
              bottom: 0;
              right: 0;
            `
          }

          background-color: ${
            isMappedOverSource ? theme.colors.MangoOrangeD10 : theme.colors.BrandPurple
          };
          &:hover {
            cursor: ${cursor}-resize;
          }
        }
      `
    }

    background-color: ${
      isMappedOverSource ? theme.colors.MangoOrangeD10 : theme.colors.BrandPurple
    };

    &:hover {
      cursor: ${cursor}-resize;
    }
  `}
`;

interface Props {
  onMouseDown: (e: MouseEvent) => void;
  width: number;
  height: number;
  isMappedOverSource: boolean;
}

const borderThickness = 1;

class RenderBorder extends React.PureComponent<any> {
  ref: React.MutableRefObject<HTMLElement | null>;

  constructor(props: any) {
    super(props);
    this.ref = React.createRef();
  }

  componentDidMount() {
    this.ref.current?.addEventListener('mousedown', this.props.onMouseDown, { capture: true });
  }

  componentWillUnmount() {
    this.ref.current?.removeEventListener('mousedown', this.props.onMouseDown, { capture: true });
  }

  render() {
    const { Component, ...otherProps } = this.props;
    return (
      <Component
        ref={this.ref}
        data-cursor={otherProps.cursor}
        data-craft-ignore-events
        {...otherProps}
      />
    );
  }
}

export class ResizeBoundaries extends React.PureComponent<Props> {
  render() {
    const { width, height, isMappedOverSource } = this.props;

    const dots = [
      {
        key: 'nw',
        'data-handle': 'nw',
        'data-axis': Axes.All,
        'data-movement': Movement.Inverted,
        cursor: 'nwse',
        style: { top: -1, left: -1 },
      },
      {
        key: 'n',
        'data-handle': 'n',
        'data-axis': Axes.Y,
        'data-movement': Movement.Inverted,
        cursor: 'ns',
        style: { top: -1, left: width / 2 - 8 },
      },
      {
        key: 'ne',
        'data-handle': 'ne',
        'data-axis': Axes.All,
        'data-movement': Movement.Regular,
        cursor: 'nesw',
        style: { top: -1, left: width - 19 + 1 },
      },
      {
        key: 'e',
        'data-handle': 'e',
        'data-axis': Axes.X,
        'data-movement': Movement.Regular,
        cursor: 'ew',
        style: { top: height / 2 - 8, left: width - 6 + 1 },
      },
      {
        key: 'se',
        'data-handle': 'se',
        'data-axis': Axes.All,
        'data-movement': Movement.Regular,
        cursor: 'nwse',
        style: { top: height - 6 + 1, left: width - 19 + 1 },
      },
      {
        key: 's',
        'data-handle': 's',
        'data-axis': Axes.Y,
        'data-movement': Movement.Regular,
        cursor: 'ns',
        style: { top: height - 6 + 1, left: width / 2 - 8 },
      },
      {
        key: 'sw',
        'data-handle': 'sw',
        'data-axis': Axes.All,
        'data-movement': Movement.Regular,
        cursor: 'nesw',
        style: { top: height - 6 + 1, left: -1 },
      },
      {
        key: 'w',
        'data-handle': 'w',
        'data-axis': Axes.X,
        'data-movement': Movement.Inverted,
        cursor: 'ew',
        style: { top: height / 2 - 8, left: -1 },
      },
    ];
    const lines = [
      {
        key: 'top',
        'data-axis': Axes.Y,
        'data-movement': Movement.Inverted,
        cursor: 'ns',
        style: { top: -1, left: 0, width, height: borderThickness },
      },
      {
        key: 'right',
        'data-axis': Axes.X,
        'data-movement': Movement.Regular,
        cursor: 'ew',
        style: { top: 0, left: width, width: borderThickness, height: height + 1 },
      },
      {
        key: 'bottom',
        'data-axis': Axes.Y,
        'data-movement': Movement.Regular,
        cursor: 'ns',
        style: { top: height, left: 0, width: width + 1, height: borderThickness },
      },
      {
        key: 'left',
        'data-axis': Axes.X,
        'data-movement': Movement.Inverted,
        cursor: 'ew',
        style: { top: 0, left: -1, width: borderThickness, height },
      },
    ];

    return (
      <div key="resize-boundaries">
        {dots.map((dot: any) => (
          <RenderBorder
            className="dot"
            Component={StyledDot}
            {...dot}
            position={dot.key}
            isMappedOverSource={isMappedOverSource}
            onMouseDown={this.props.onMouseDown}
          />
        ))}
        {lines.map((line) => (
          <RenderBorder
            className="line"
            Component={StyledLine}
            {...line}
            isMappedOverSource={isMappedOverSource}
            onMouseDown={this.props.onMouseDown}
          />
        ))}
      </div>
    );
  }
}
